import { AfterViewInit, Component, ElementRef, OnDestroy, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';

@Component({
  selector: 'app-aymudo-resources-page',
  imports: [RouterLink],
  template: `
    <main class="resources-page">
      <section class="resources-hero">
        <p>{{ copy().eyebrow }}</p>
        <h1>{{ copy().title }}</h1>
        <span>{{ copy().lead }}</span>
        <nav>
          <a class="primary" [routerLink]="i18n.routePath('subscribe')">{{ copy().subscribe }}</a
          ><a href="#hikayeler">{{ copy().stories }} ↓</a>
        </nav>
        <div class="resource-lines" aria-hidden="true"><i></i><i></i><i></i><b>A</b></div>
      </section>

      <section class="story-head" id="hikayeler">
        <p>{{ copy().storyEyebrow }}</p>
        <h2>{{ copy().storyTitle }}</h2>
        <span>{{ copy().storyLead }}</span>
      </section>
      <section class="story-grid">
        @for (story of stories; track story.name; let index = $index) {
          <a [href]="story.url" target="_blank" rel="noopener" [class.featured]="index === 0">
            <figure>
              <img [src]="story.image" [alt]="story.name" loading="lazy" decoding="async" />
              <figcaption>
                <small>{{ story.type }}</small
                ><strong>{{ story.name }}</strong
                ><b>↗</b>
              </figcaption>
            </figure>
          </a>
        }
      </section>

      <section class="guide-section">
        <header>
          <p>{{ copy().guideEyebrow }}</p>
          <h2>{{ copy().guideTitle }}</h2>
        </header>
        <div class="guide-groups">
          @for (group of copy().groups; track group.title; let groupIndex = $index) {
            <article>
              <header>
                <span>0{{ groupIndex + 1 }}</span>
                <h3>{{ group.title }}</h3>
              </header>
              <nav>
                @for (item of group.items; track item.title) {
                  <a [routerLink]="i18n.localizedPath(item.path)"
                    ><span
                      ><strong>{{ item.title }}</strong
                      ><small>{{ item.text }}</small></span
                    ><b>→</b></a
                  >
                }
              </nav>
            </article>
          }
        </div>
      </section>

      <section class="updates">
        <header>
          <p>{{ copy().updateEyebrow }}</p>
          <h2>{{ copy().updateTitle }}</h2>
          <span>{{ copy().updateLead }}</span>
        </header>
        <div>
          @for (item of copy().updates; track item.title; let index = $index) {
            <article>
              <small>UPDATE / 0{{ index + 1 }}</small>
              <h3>{{ item.title }}</h3>
              <p>{{ item.text }}</p>
              <a [routerLink]="i18n.localizedPath(item.path)">{{ copy().explore }} →</a>
            </article>
          }
        </div>
      </section>

      <section class="resource-close">
        <p>{{ copy().closeEyebrow }}</p>
        <h2>{{ copy().closeTitle }}</h2>
        <div>
          <a [routerLink]="i18n.routePath('subscribe')">{{ copy().subscribe }}</a
          ><a [routerLink]="i18n.routePath('products')">{{ copy().products }} →</a>
        </div>
      </section>
    </main>
  `,
  styleUrl: './aymudo-resources.page.scss',
})
export class AymudoResourcesPage implements AfterViewInit, OnDestroy {
  protected readonly i18n = inject(I18nService);
  private readonly seo = inject(SeoService);
  private readonly host = inject(ElementRef<HTMLElement>);
  protected readonly copy = computed(() => {
    const lang = this.i18n.contentLang();
    return COPY[lang] ?? { ...COPY['en'], ...(RESOURCE_CORE[lang] ?? {}) };
  });
  protected readonly stories = [
    {
      name: 'CEON Dent',
      type: 'HEALTH / APPOINTMENT',
      url: 'https://www.ceondent.com',
      image: '/marketing/aymudo/screens/ceondent-desktop.webp',
    },
    {
      name: 'Umut Demir',
      type: 'SERVICE / TEAM',
      url: 'https://www.umutdemironline.com',
      image: '/marketing/aymudo/screens/umutdemir-desktop.webp',
    },
    {
      name: 'Fal Deryası',
      type: 'LIVE / MARKETPLACE',
      url: 'https://www.falderyasi.com',
      image: '/marketing/aymudo/screens/falderyasi-desktop.webp',
    },
    {
      name: 'BDSMBeds',
      type: 'GLOBAL / COMMERCE',
      url: 'https://www.bdsmbeds.com',
      image: '/marketing/aymudo/screens/bdsmbeds-desktop.webp',
    },
  ];
  private context?: { revert(): void };
  constructor() {
    const copy = this.copy();
    this.seo.setPage({
      title: copy.seoTitle,
      description: copy.lead,
      path: this.i18n.localizedPath('/kaynaklar'),
    });
  }
  async ngAfterViewInit(): Promise<void> {
    if (
      typeof window === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    const [{ gsap }, { ScrollTrigger }] = await Promise.all([
      import('gsap'),
      import('gsap/ScrollTrigger'),
    ]);
    gsap.registerPlugin(ScrollTrigger);
    this.context = gsap.context(() => {
      gsap.from('.resources-hero>*:not(.resource-lines)', {
        y: 22,
        stagger: 0.07,
        duration: 0.65,
        ease: 'power3.out',
        clearProps: 'transform',
      });
      gsap.utils
        .toArray<HTMLElement>('.story-grid a,.guide-groups article,.updates article')
        .forEach((item) =>
          gsap.from(item, {
            y: 24,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: { trigger: item, start: 'top 92%', once: true },
            clearProps: 'transform',
          }),
        );
    }, this.host.nativeElement);
  }
  ngOnDestroy(): void {
    this.context?.revert();
  }
}

const COPY: Record<string, any> = {
  tr: {
    eyebrow: 'Aymudo kaynakları',
    title: 'Çalışan sistemlerden öğrenin.',
    lead: 'Gerçek işletme hikâyelerini inceleyin, ihtiyacınıza uygun ürünü bulun ve Aymudo’nun nasıl çalıştığını adım adım görün.',
    subscribe: 'Aymudo’ya abone ol',
    stories: 'Müşteri hikâyeleri',
    storyEyebrow: 'Gerçek işletmeler',
    storyTitle: 'Aynı altyapı. Birbirine benzemeyen işler.',
    storyLead:
      'Her örnek farklı bir iş modelinin Aymudo üzerinde nasıl kendi markasına ve operasyonuna dönüştüğünü gösterir.',
    guideEyebrow: 'Ürün rehberleri',
    guideTitle: 'Nereden başlayacağınıza göre ilerleyin.',
    groups: [
      {
        title: 'Kur ve yayınla',
        items: [
          {
            title: 'Website ve yayın sistemi',
            text: 'Markanızı ve alan adınızı yayına çıkarın.',
            path: '/urunler/web-sitesi',
          },
          {
            title: 'Tema motoru',
            text: 'Tasarımı panelden kendi markanıza dönüştürün.',
            path: '/urunler/tema-motoru',
          },
          {
            title: 'Dil ve ülke',
            text: 'Yerel deneyimleri tek sitede yönetin.',
            path: '/urunler/yerellestirme',
          },
        ],
      },
      {
        title: 'Yönet ve büyüt',
        items: [
          {
            title: 'Randevular',
            text: 'Zamanı, personeli ve kaynakları eşleştirin.',
            path: '/urunler/randevu',
          },
          {
            title: 'Şube ve personel',
            text: 'Ekibi ve çalışma alanlarını bağlayın.',
            path: '/urunler/sube-personel',
          },
          {
            title: 'Tetikleyiciler',
            text: 'Tekrarlanan işleri otomatikleştirin.',
            path: '/urunler/tetikleyiciler',
          },
        ],
      },
      {
        title: 'Sat ve ölçekle',
        items: [
          {
            title: 'E-ticaret',
            text: 'Üründen teslimata tek sipariş akışı kurun.',
            path: '/urunler/e-ticaret',
          },
          {
            title: 'Pazaryeri',
            text: 'Satıcı, komisyon ve siparişleri yönetin.',
            path: '/urunler/pazaryeri',
          },
          {
            title: 'Global satış',
            text: 'Dil, para birimi, depo ve kargoyla açılın.',
            path: '/urunler/global-satis',
          },
        ],
      },
    ],
    updateEyebrow: 'Ürün günlüğü',
    updateTitle: 'Sistem sürekli ilerler.',
    updateLead:
      'Yeni yetenekler ayrı bir araç olarak değil, kullandığınız işletme sisteminin devamı olarak gelir.',
    updates: [
      {
        title: 'Tema yönetimi artık ürün hikâyesinin merkezinde',
        text: 'Markaya özel tasarım ile panelden içerik yönetiminin birlikte nasıl çalıştığını görün.',
        path: '/urunler/tema-motoru',
      },
      {
        title: 'Global satış tek yapı altında',
        text: 'Dil, RTL, para birimi, depo ve kargo profilleri aynı pazara hazırlanma akışında.',
        path: '/urunler/global-satis',
      },
      {
        title: 'Personel ekranları görev kadar sade',
        text: 'Her çalışana yalnızca ihtiyacı olan ekranı ve işlem yetkisini atayın.',
        path: '/urunler/ekran-yetki',
      },
    ],
    explore: 'İncele',
    closeEyebrow: 'İhtiyacınız olan yerden başlayın',
    closeTitle: 'Okumaktan uygulamaya geçin.',
    products: 'Tüm ürünler',
    seoTitle: 'Aymudo Kaynakları | Rehberler ve müşteri hikâyeleri',
  },
  en: {
    eyebrow: 'Aymudo resources',
    title: 'Learn from systems that already work.',
    lead: 'Explore real business stories, find the right product and see how Aymudo works step by step.',
    subscribe: 'Subscribe to Aymudo',
    stories: 'Customer stories',
    storyEyebrow: 'Real businesses',
    storyTitle: 'One infrastructure. Businesses that look nothing alike.',
    storyLead:
      'Every example shows how a different business model becomes its own brand and operation on Aymudo.',
    guideEyebrow: 'Product guides',
    guideTitle: 'Move forward from where you need to start.',
    groups: [
      {
        title: 'Build and publish',
        items: [
          {
            title: 'Website and publishing',
            text: 'Launch the brand and domain.',
            path: '/urunler/web-sitesi',
          },
          {
            title: 'Theme engine',
            text: 'Shape the design from the workspace.',
            path: '/urunler/tema-motoru',
          },
          {
            title: 'Language and country',
            text: 'Manage local experiences on one site.',
            path: '/urunler/yerellestirme',
          },
        ],
      },
      {
        title: 'Manage and grow',
        items: [
          {
            title: 'Appointments',
            text: 'Connect time, people and resources.',
            path: '/urunler/randevu',
          },
          {
            title: 'Branches and staff',
            text: 'Connect teams and workspaces.',
            path: '/urunler/sube-personel',
          },
          { title: 'Triggers', text: 'Automate repeated work.', path: '/urunler/tetikleyiciler' },
        ],
      },
      {
        title: 'Sell and scale',
        items: [
          {
            title: 'Commerce',
            text: 'One order flow from product to delivery.',
            path: '/urunler/e-ticaret',
          },
          {
            title: 'Marketplace',
            text: 'Manage sellers, commissions and orders.',
            path: '/urunler/pazaryeri',
          },
          {
            title: 'Global selling',
            text: 'Expand with language, currency and shipping.',
            path: '/urunler/global-satis',
          },
        ],
      },
    ],
    updateEyebrow: 'Product journal',
    updateTitle: 'The system keeps moving.',
    updateLead: 'New capabilities arrive as part of the business system you already use.',
    updates: [
      {
        title: 'Theme management at the centre of the story',
        text: 'See custom design and editable content working together.',
        path: '/urunler/tema-motoru',
      },
      {
        title: 'Global selling in one structure',
        text: 'Language, RTL, currency, warehouse and shipping profiles work together.',
        path: '/urunler/global-satis',
      },
      {
        title: 'Staff screens as simple as the role',
        text: 'Assign only the workspace and actions each person needs.',
        path: '/urunler/ekran-yetki',
      },
    ],
    explore: 'Explore',
    closeEyebrow: 'Start where you need',
    closeTitle: 'Move from reading to running.',
    products: 'All products',
    seoTitle: 'Aymudo Resources | Guides and customer stories',
  },
};

const RESOURCE_CORE: Record<string, Record<string, string>> = {
  de: {
    eyebrow: 'Aymudo Ressourcen',
    title: 'Lernen Sie von Systemen, die bereits funktionieren.',
    lead: 'Entdecken Sie echte Unternehmensgeschichten, finden Sie das passende Produkt und erleben Sie Aymudo Schritt für Schritt.',
    subscribe: 'Aymudo abonnieren',
    stories: 'Kundengeschichten',
    storyEyebrow: 'Echte Unternehmen',
    storyTitle: 'Eine Infrastruktur. Unternehmen, die völlig verschieden aussehen.',
    storyLead:
      'Jedes Beispiel zeigt, wie ein Geschäftsmodell auf Aymudo zur eigenen Marke und zum eigenen Betrieb wird.',
    guideEyebrow: 'Produktleitfäden',
    guideTitle: 'Starten Sie dort, wo Ihr Bedarf beginnt.',
    updateEyebrow: 'Produktjournal',
    updateTitle: 'Das System entwickelt sich ständig weiter.',
    updateLead: 'Neue Funktionen werden Teil des Systems, das Sie bereits nutzen.',
    explore: 'Entdecken',
    closeEyebrow: 'Starten Sie dort, wo Sie es brauchen',
    closeTitle: 'Vom Lesen zum Umsetzen.',
    products: 'Alle Produkte',
  },
  es: {
    eyebrow: 'Recursos de Aymudo',
    title: 'Aprende de sistemas que ya funcionan.',
    lead: 'Explora historias reales, encuentra el producto adecuado y descubre Aymudo paso a paso.',
    subscribe: 'Suscríbete a Aymudo',
    stories: 'Historias de clientes',
    storyEyebrow: 'Negocios reales',
    storyTitle: 'Una infraestructura. Negocios que no se parecen.',
    storyLead:
      'Cada ejemplo muestra cómo un modelo diferente se convierte en su propia marca y operación.',
    guideEyebrow: 'Guías de producto',
    guideTitle: 'Avanza desde el punto que necesitas.',
    updateEyebrow: 'Diario de producto',
    updateTitle: 'El sistema sigue avanzando.',
    updateLead: 'Las nuevas capacidades llegan como parte del sistema que ya utilizas.',
    explore: 'Explorar',
    closeEyebrow: 'Empieza donde lo necesites',
    closeTitle: 'Pasa de leer a operar.',
    products: 'Todos los productos',
  },
  fr: {
    eyebrow: 'Ressources Aymudo',
    title: 'Apprenez de systèmes qui fonctionnent déjà.',
    lead: 'Découvrez de vraies entreprises, trouvez le bon produit et comprenez Aymudo étape par étape.',
    subscribe: 'S’abonner à Aymudo',
    stories: 'Histoires clients',
    storyEyebrow: 'Entreprises réelles',
    storyTitle: 'Une infrastructure. Des entreprises qui ne se ressemblent pas.',
    storyLead:
      'Chaque exemple montre comment un modèle devient sa propre marque et son propre fonctionnement.',
    guideEyebrow: 'Guides produits',
    guideTitle: 'Avancez depuis votre point de départ.',
    updateEyebrow: 'Journal produit',
    updateTitle: 'Le système continue d’évoluer.',
    updateLead: 'Les nouvelles capacités rejoignent le système que vous utilisez déjà.',
    explore: 'Découvrir',
    closeEyebrow: 'Commencez là où vous en avez besoin',
    closeTitle: 'Passez de la lecture à l’action.',
    products: 'Tous les produits',
  },
  ar: {
    eyebrow: 'موارد أيمودو',
    title: 'تعلّم من أنظمة تعمل بالفعل.',
    lead: 'استكشف قصص أعمال حقيقية، واختر المنتج المناسب، وتعرّف إلى أيمودو خطوة بخطوة.',
    subscribe: 'اشترك في أيمودو',
    stories: 'قصص العملاء',
    storyEyebrow: 'أعمال حقيقية',
    storyTitle: 'بنية واحدة، وأعمال لا تشبه بعضها.',
    storyLead: 'يوضح كل مثال كيف يتحول نموذج عمل مختلف إلى علامته وعملياته الخاصة على أيمودو.',
    guideEyebrow: 'أدلة المنتجات',
    guideTitle: 'تقدم من نقطة البداية التي تحتاجها.',
    updateEyebrow: 'سجل المنتج',
    updateTitle: 'النظام يتطور باستمرار.',
    updateLead: 'تصل القدرات الجديدة كجزء من نظام الأعمال الذي تستخدمه بالفعل.',
    explore: 'استكشف',
    closeEyebrow: 'ابدأ من حيث تحتاج',
    closeTitle: 'انتقل من القراءة إلى التشغيل.',
    products: 'كل المنتجات',
  },
  pt: {
    eyebrow: 'Recursos Aymudo',
    title: 'Aprenda com sistemas que já funcionam.',
    lead: 'Explore histórias reais, encontre o produto certo e veja como Aymudo funciona passo a passo.',
    subscribe: 'Assinar Aymudo',
    stories: 'Histórias de clientes',
    storyEyebrow: 'Negócios reais',
    storyTitle: 'Uma infraestrutura. Negócios completamente diferentes.',
    storyLead: 'Cada exemplo mostra como um modelo se torna sua própria marca e operação.',
    guideEyebrow: 'Guias de produto',
    guideTitle: 'Avance a partir de onde precisa começar.',
    updateEyebrow: 'Diário do produto',
    updateTitle: 'O sistema continua evoluindo.',
    updateLead: 'Novos recursos chegam como parte do sistema que você já usa.',
    explore: 'Explorar',
    closeEyebrow: 'Comece de onde precisa',
    closeTitle: 'Passe da leitura à operação.',
    products: 'Todos os produtos',
  },
  ru: {
    eyebrow: 'Ресурсы Aymudo',
    title: 'Учитесь на системах, которые уже работают.',
    lead: 'Изучайте реальные истории, находите нужный продукт и знакомьтесь с Aymudo шаг за шагом.',
    subscribe: 'Подписаться на Aymudo',
    stories: 'Истории клиентов',
    storyEyebrow: 'Реальный бизнес',
    storyTitle: 'Одна инфраструктура. Совершенно разные компании.',
    storyLead:
      'Каждый пример показывает, как модель превращается в собственный бренд и рабочий процесс.',
    guideEyebrow: 'Руководства по продуктам',
    guideTitle: 'Двигайтесь от нужной вам точки старта.',
    updateEyebrow: 'Журнал продукта',
    updateTitle: 'Система постоянно развивается.',
    updateLead: 'Новые возможности становятся частью уже используемой бизнес-системы.',
    explore: 'Подробнее',
    closeEyebrow: 'Начните с нужного вам',
    closeTitle: 'Переходите от чтения к работе.',
    products: 'Все продукты',
  },
  zh: {
    eyebrow: 'Aymudo 资源',
    title: '向已经有效运行的系统学习。',
    lead: '查看真实企业故事，找到合适产品，并逐步了解 Aymudo 的工作方式。',
    subscribe: '订阅 Aymudo',
    stories: '客户故事',
    storyEyebrow: '真实企业',
    storyTitle: '一套基础设施，呈现完全不同的企业。',
    storyLead: '每个案例都展示不同业务模式如何在 Aymudo 上形成自己的品牌与运营。',
    guideEyebrow: '产品指南',
    guideTitle: '从你需要的起点继续前进。',
    updateEyebrow: '产品日志',
    updateTitle: '系统持续进化。',
    updateLead: '新能力会成为你已在使用的业务系统的一部分。',
    explore: '探索',
    closeEyebrow: '从你需要的地方开始',
    closeTitle: '从阅读走向实际运营。',
    products: '所有产品',
  },
  hi: {
    eyebrow: 'Aymudo संसाधन',
    title: 'पहले से काम कर रहे सिस्टम से सीखें।',
    lead: 'वास्तविक व्यवसाय की कहानियां देखें, सही उत्पाद खोजें और Aymudo को चरण-दर-चरण समझें।',
    subscribe: 'Aymudo की सदस्यता लें',
    stories: 'ग्राहक कहानियां',
    storyEyebrow: 'वास्तविक व्यवसाय',
    storyTitle: 'एक आधार। बिल्कुल अलग दिखने वाले व्यवसाय।',
    storyLead: 'हर उदाहरण दिखाता है कि अलग मॉडल Aymudo पर अपना ब्रांड और संचालन कैसे बनाता है।',
    guideEyebrow: 'उत्पाद गाइड',
    guideTitle: 'जहाँ से जरूरत हो वहाँ से आगे बढ़ें।',
    updateEyebrow: 'उत्पाद जर्नल',
    updateTitle: 'सिस्टम लगातार आगे बढ़ता है।',
    updateLead: 'नई क्षमताएं आपके मौजूदा व्यवसाय सिस्टम का हिस्सा बनकर आती हैं।',
    explore: 'देखें',
    closeEyebrow: 'जहाँ जरूरत हो वहाँ से शुरू करें',
    closeTitle: 'पढ़ने से संचालन की ओर बढ़ें।',
    products: 'सभी उत्पाद',
  },
  ja: {
    eyebrow: 'Aymudo リソース',
    title: 'すでに機能しているシステムから学ぶ。',
    lead: '実際の事例を見て、最適な製品を見つけ、Aymudoの仕組みを順に理解できます。',
    subscribe: 'Aymudoを申し込む',
    stories: 'お客様事例',
    storyEyebrow: '実際のビジネス',
    storyTitle: 'ひとつの基盤。まったく異なるビジネス。',
    storyLead: '異なるモデルがAymudo上で独自のブランドと運営になる過程を紹介します。',
    guideEyebrow: '製品ガイド',
    guideTitle: '必要な出発点から進めます。',
    updateEyebrow: '製品ジャーナル',
    updateTitle: 'システムは進化し続けます。',
    updateLead: '新機能は、すでに使っている事業システムの一部として届きます。',
    explore: '詳しく見る',
    closeEyebrow: '必要な場所から始める',
    closeTitle: '読むことから、動かすことへ。',
    products: 'すべての製品',
  },
  ko: {
    eyebrow: 'Aymudo 리소스',
    title: '이미 제대로 작동하는 시스템에서 배우세요.',
    lead: '실제 비즈니스 사례를 살펴보고 적합한 제품과 Aymudo의 작동 방식을 단계별로 확인하세요.',
    subscribe: 'Aymudo 구독하기',
    stories: '고객 사례',
    storyEyebrow: '실제 비즈니스',
    storyTitle: '하나의 기반. 서로 전혀 다른 비즈니스.',
    storyLead:
      '각 사례는 다른 모델이 Aymudo에서 고유한 브랜드와 운영으로 바뀌는 과정을 보여줍니다.',
    guideEyebrow: '제품 가이드',
    guideTitle: '필요한 시작점에서 앞으로 나아가세요.',
    updateEyebrow: '제품 저널',
    updateTitle: '시스템은 계속 진화합니다.',
    updateLead: '새 기능은 이미 사용하는 비즈니스 시스템의 일부로 제공됩니다.',
    explore: '살펴보기',
    closeEyebrow: '필요한 곳에서 시작하세요',
    closeTitle: '읽기에서 실제 운영으로.',
    products: '모든 제품',
  },
  it: {
    eyebrow: 'Risorse Aymudo',
    title: 'Impara da sistemi che funzionano già.',
    lead: 'Scopri storie reali, trova il prodotto giusto e guarda Aymudo in azione passo dopo passo.',
    subscribe: 'Abbonati ad Aymudo',
    stories: 'Storie dei clienti',
    storyEyebrow: 'Imprese reali',
    storyTitle: 'Un’infrastruttura. Imprese completamente diverse.',
    storyLead:
      'Ogni esempio mostra come un modello diventa il proprio brand e la propria operatività.',
    guideEyebrow: 'Guide ai prodotti',
    guideTitle: 'Procedi dal punto da cui devi iniziare.',
    updateEyebrow: 'Diario del prodotto',
    updateTitle: 'Il sistema continua a evolvere.',
    updateLead: 'Le nuove capacità arrivano come parte del sistema che usi già.',
    explore: 'Scopri',
    closeEyebrow: 'Inizia da ciò che ti serve',
    closeTitle: 'Dalla lettura all’operatività.',
    products: 'Tutti i prodotti',
  },
  nl: {
    eyebrow: 'Aymudo bronnen',
    title: 'Leer van systemen die al werken.',
    lead: 'Bekijk echte bedrijfsverhalen, vind het juiste product en ontdek stap voor stap hoe Aymudo werkt.',
    subscribe: 'Abonneer op Aymudo',
    stories: 'Klantverhalen',
    storyEyebrow: 'Echte bedrijven',
    storyTitle: 'Eén infrastructuur. Bedrijven die totaal niet op elkaar lijken.',
    storyLead:
      'Elk voorbeeld toont hoe een ander model op Aymudo een eigen merk en operatie wordt.',
    guideEyebrow: 'Productgidsen',
    guideTitle: 'Ga verder vanaf het punt waar u wilt beginnen.',
    updateEyebrow: 'Productjournaal',
    updateTitle: 'Het systeem blijft zich ontwikkelen.',
    updateLead: 'Nieuwe mogelijkheden worden onderdeel van het bedrijfssysteem dat u al gebruikt.',
    explore: 'Ontdek',
    closeEyebrow: 'Begin waar u het nodig hebt',
    closeTitle: 'Van lezen naar uitvoeren.',
    products: 'Alle producten',
  },
  id: {
    eyebrow: 'Sumber daya Aymudo',
    title: 'Belajar dari sistem yang sudah bekerja.',
    lead: 'Jelajahi kisah bisnis nyata, temukan produk yang tepat, dan lihat cara kerja Aymudo langkah demi langkah.',
    subscribe: 'Berlangganan Aymudo',
    stories: 'Kisah pelanggan',
    storyEyebrow: 'Bisnis nyata',
    storyTitle: 'Satu infrastruktur. Bisnis yang sangat berbeda.',
    storyLead:
      'Setiap contoh menunjukkan bagaimana model berbeda menjadi merek dan operasinya sendiri.',
    guideEyebrow: 'Panduan produk',
    guideTitle: 'Lanjutkan dari titik yang Anda butuhkan.',
    updateEyebrow: 'Jurnal produk',
    updateTitle: 'Sistem terus berkembang.',
    updateLead: 'Kemampuan baru hadir sebagai bagian dari sistem bisnis yang sudah Anda gunakan.',
    explore: 'Jelajahi',
    closeEyebrow: 'Mulai dari yang Anda butuhkan',
    closeTitle: 'Dari membaca ke menjalankan.',
    products: 'Semua produk',
  },
};
